/**
 * Deal scoring.
 *
 * The whole point of the tool: decide whether a listing is worth buying, given
 * what a resale would actually pay out. Torn takes 5% from the seller on every
 * item-market sale, and another 10% if the listing is anonymous — so scoring
 * against the raw market average overstates every margin.
 */

import type { DealTier, Offer, Settings, WatchItem } from "../state/types.ts";
import { clamp } from "../util/format.ts";

export const MARKET_FEE_PCT = 5;
export const ANON_FEE_PCT = 10;

/** Combined seller-side fee as a fraction, from the two toggles. */
export function feeRate(settings: Pick<Settings, "marketFeeEnabled" | "anonFeeEnabled">): number {
  if (!settings.marketFeeEnabled) return 0;
  return (MARKET_FEE_PCT + (settings.anonFeeEnabled ? ANON_FEE_PCT : 0)) / 100;
}

/** What a resale at market average actually nets after fees. */
export function netValue(marketValue: number, fee = 0): number {
  if (marketValue <= 0) return 0;
  return marketValue * (1 - clamp(fee, 0, 1));
}

/**
 * Green = worth buying, orange = below net value but not by enough,
 * red = no edge.
 *
 * A per-row `maxBuyPrice` replaces the profit-% test entirely: the user has
 * named their own number, so honour it rather than second-guessing it.
 */
export function dealTier(
  price: number,
  marketValue: number,
  profitPct: number,
  maxBuyPrice: number | null,
  fee = 0,
): DealTier {
  const net = netValue(marketValue, fee);

  if (maxBuyPrice !== null && maxBuyPrice > 0) {
    if (price <= maxBuyPrice) return "green";
    return net > 0 && price < net ? "orange" : "red";
  }

  if (marketValue <= 0) return "red";
  if (price <= net * (1 - profitPct / 100)) return "green";
  if (price < net) return "orange";
  return "red";
}

/** Signed distance from net value. Negative means the price is a discount. */
export function margin(price: number, marketValue: number, fee = 0): number {
  const net = netValue(marketValue, fee);
  if (net <= 0) return 0;
  return (price - net) / net;
}

/** Stable identity for an offer, used to alert once per distinct listing. */
export function offerKey(offer: Pick<Offer, "itemId" | "source" | "sellerId" | "price">): string {
  return `${offer.itemId}:${offer.source}:${offer.sellerId ?? "-"}:${offer.price}`;
}

/** The row's own minimum quantity, falling back to the global setting. */
export function effectiveMinUnits(item: WatchItem, settings: Settings): number {
  const value = item.minUnits ?? settings.minUnits;
  return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 1;
}

/** The row's own poll interval, falling back to the global setting. */
export function effectiveIntervalSec(item: WatchItem, settings: Settings): number {
  const value = item.intervalSec ?? settings.intervalSec;
  return Number.isFinite(value) && value > 0 ? value : settings.intervalSec;
}

/**
 * Filters a listing out before it can alert, become the row's best price, or
 * get a BUY link. Applied identically to market and bazaar offers.
 */
export function offerQualifies(
  offer: Offer,
  item: WatchItem,
  settings: Settings,
  isIgnored: (key: string) => boolean,
): boolean {
  if (offer.price <= 0) return false;
  // Display-case entries are visible on weav3r but cannot be bought.
  if (offer.source === "display") return false;
  if (offer.quantity > 0 && offer.quantity < effectiveMinUnits(item, settings)) return false;
  if (settings.onlyUnderMv && item.marketValue > 0 && offer.price >= item.marketValue) return false;
  if (isIgnored(offerKey(offer))) return false;
  return true;
}

/** Cheapest first; ties broken by the larger stack. */
export function cheapest(offers: Offer[]): Offer | null {
  let best: Offer | null = null;
  for (const offer of offers) {
    if (best === null || offer.price < best.price) {
      best = offer;
    } else if (offer.price === best.price && offer.quantity > best.quantity) {
      best = offer;
    }
  }
  return best;
}
